import type { EventPageEvent } from '../data/types'
import { truthy } from '../data/types'
import { sanitizeRichText } from '../data/richText'
import { EASTERN } from '../data/format'
import styles from './ScheduleSection.module.css'

interface ScheduleItem {
  time: string
  label: string
  strong?: boolean
}

interface ScheduleDay {
  key: string
  dow: string
  day: string
  items: ScheduleItem[]
}

// Milestones are entered/stored as Eastern wall-clock time (see data/format.ts) - grouping and
// labeling by calendar day must use the Eastern calendar date, not the viewer's local one, or a
// milestone near local midnight can land in the wrong day bucket / show the wrong day number.
const dayKeyFormatter = new Intl.DateTimeFormat('en-CA', { timeZone: EASTERN })
const dayLabelFormatter = new Intl.DateTimeFormat('en-US', { timeZone: EASTERN, weekday: 'short', day: 'numeric' })

function easternDayLabel(d: Date): { dow: string; day: string } {
  const parts = dayLabelFormatter.formatToParts(d)
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? ''
  return { dow: get('weekday').toUpperCase(), day: get('day') }
}

/** The mockup envisions a full day-by-day timeline; the schema only has 5 fixed milestone
 *  datetimes (or a free-text override) - see the plan's "fast-follow" note on structured
 *  schedule data. This buckets whatever milestones actually exist by calendar day, so a
 *  multi-day event still gets one card per real day rather than fabricating a day grid. */
function buildScheduleDays(event: EventPageEvent): ScheduleDay[] {
  const raw: Array<{ dt: string; label: string; strong?: boolean }> = [
    { dt: event.openPracticeStart, label: 'Open practice starts' },
    { dt: event.openPracticeEnd, label: 'Open practice ends' },
    { dt: event.closedPracticeStart, label: 'Controlled practice starts' },
    { dt: event.qualifyingStart, label: 'Qualifying starts', strong: true },
    { dt: event.mainsStart, label: 'Mains start', strong: true },
  ].filter((x) => x.dt)

  const byDay = new Map<string, { date: Date; items: ScheduleItem[] }>()
  for (const r of raw) {
    const d = new Date(r.dt)
    if (Number.isNaN(d.getTime())) continue
    const key = dayKeyFormatter.format(d)
    if (!byDay.has(key)) byDay.set(key, { date: d, items: [] })
    byDay.get(key)!.items.push({
      // Same literal " ET" suffix convention as data/format.ts's formatDateTime - see its comment.
      time: `${d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', timeZone: EASTERN })} ET`,
      label: r.label,
      strong: r.strong,
    })
  }

  return Array.from(byDay.entries())
    .sort((a, b) => a[1].date.getTime() - b[1].date.getTime())
    .map(([key, { date, items }]) => ({
      key,
      ...easternDayLabel(date),
      items,
    }))
}

export function ScheduleSection({ event }: { event: EventPageEvent }) {
  if (truthy(event.hideHeats) && event.manualSchedule) {
    // Rich text (Quill/CKEditor), same trust boundary as event.description - see App.tsx.
    // eslint-disable-next-line react/no-danger
    return <div className={styles.prose} dangerouslySetInnerHTML={{ __html: sanitizeRichText(event.manualSchedule) }} />
  }

  const days = buildScheduleDays(event)
  if (!days.length) return null

  return (
    <div className={styles.days}>
      {days.map((d) => (
        <div key={d.key} className={styles.dayCard}>
          <div className={styles.dateBadge}>
            <span className={styles.dow}>{d.dow}</span>
            <span className={styles.dayNum}>{d.day}</span>
          </div>
          <div className={styles.items}>
            {d.items.map((it, i) => (
              <div key={i} className={styles.item}>
                <span className={styles.itemLabel} style={{ fontWeight: it.strong ? 700 : 500 }}>
                  {it.label}
                </span>
                <span className={styles.itemTime}>{it.time}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}
